import { useEffect, useMemo, useState } from "react";

/**
 * Prosty hook do zapisywania identyfikatorów ulubionych przepisów na urządzeniu użytkownika.
 * - Brak logowania, dane nie opuszczają urządzenia (localStorage)
 * - Można podpiąć do ikonki "gwiazdki" na karcie przepisu
 *
 * Użycie:
 *   const { favorites, isFavorite, toggleFavorite, clearFavorites } = useFavorites();
 *   <button onClick={() => toggleFavorite(recipe.id)}>
 *     {isFavorite(recipe.id) ? "★" : "☆"}
 *   </button>
 */
export default function useFavorites(storageKey = "favorites_v1") {
  const [favorites, setFavorites] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  });

  // Zapisuj do localStorage przy każdej zmianie
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(favorites));
    } catch {
      // ciche pominięcie (np. tryb prywatny/ograniczenia storage)
    }
  }, [favorites, storageKey]);

  // Szybkie sprawdzanie, czy dany id jest ulubiony
  const isFavorite = useMemo(() => {
    // favorites może teraz zawierać obiekty lub stringi (id)
    const ids = favorites.map(f => typeof f === 'object' ? f.id : f);
    const set = new Set(ids);
    return (id) => set.has(id);
  }, [favorites]);

  // Przełącz ulubiony/nieulubiony
  const toggleFavorite = (recipeOrId) => {
    if (!recipeOrId) return;
    const id = typeof recipeOrId === 'object' ? recipeOrId.id : recipeOrId;

    setFavorites((prev) => {
      const ids = prev.map(f => typeof f === 'object' ? f.id : f);
      const set = new Set(ids);

      if (set.has(id)) {
        return prev.filter((x) => (typeof x === 'object' ? x.id : x) !== id);
      } else {
        return [...prev, recipeOrId];
      }
    });
  };

  // Wyczyść wszystkie ulubione (np. w Ustawieniach)
  const clearFavorites = () => setFavorites([]);

  return { favorites, setFavorites, isFavorite, toggleFavorite, clearFavorites };
}
